import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/sclogo-250-1.svg';

/**
 * TenantDetails Component
 * Full-page tenant details and editing screen
 */
const TenantDetails = () => {
    const { tenantId } = useParams();
    const navigate = useNavigate();
    const [tenant, setTenant] = useState(null);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        status: 'active',
        isAdmin: false,
        vendorDashboard: '',
        defaultVendorId: ''
    });
    const [showAddUser, setShowAddUser] = useState(false);
    const [newUserData, setNewUserData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user'
    });
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [vendors, setVendors] = useState([]);

    useEffect(() => {
        loadTenantDetails();
        loadVendors();
    }, [tenantId]);

    const loadVendors = async () => {
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch('/api/snowflake/vendors', {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            const data = await response.json();
            if (data.success && data.vendors) {
                setVendors(data.vendors);
                console.log(`✅ Loaded ${data.vendors.length} vendors from Snowflake`);
            } else {
                console.error('Failed to load vendors:', data.message);
                setVendors([]);
            }
        } catch (error) {
            console.error('Error loading vendors:', error);
            setVendors([]);
        }
    };

    const loadTenantDetails = async () => {
        try {
            setIsLoading(true);
            
            // Fetch tenant data from the tenants list (in a real app, you'd have a dedicated endpoint)
            const tenantsResponse = await fetch('/api/admin/tenants', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (!tenantsResponse.ok) {
                throw new Error('Failed to fetch tenant data');
            }
            
            const tenantsData = await tenantsResponse.json();
            const foundTenant = tenantsData.tenants.find(t => t.id === parseInt(tenantId));
            
            if (!foundTenant) {
                throw new Error('Tenant not found');
            }
            
            setTenant(foundTenant);
                setFormData({
                    name: foundTenant.name,
                    domain: foundTenant.domain,
                    status: foundTenant.status,
                    isAdmin: foundTenant.isAdmin,
                    vendorDashboard: foundTenant.vendorDashboard || '',
                    defaultVendorId: foundTenant.defaultVendorId || ''
                });
            
            // Fetch users for this tenant
            const usersResponse = await fetch(`/api/tenant/${tenantId}/users`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData.success ? usersData.users : []);
            }
            
        } catch (error) {
            console.error('Failed to load tenant details:', error);
            alert('Failed to load tenant details. Redirecting to admin panel.');
            navigate('/admin');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        
        try {
            const response = await fetch(`/api/admin/tenants/${tenantId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    name: formData.name,
                    domain: formData.domain,
                    status: formData.status,
                    isAdmin: formData.isAdmin,
                    vendorDashboard: formData.vendorDashboard,
                    defaultVendorId: formData.defaultVendorId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                navigate('/admin');
            } else {
                throw new Error(data.message || 'Failed to update tenant');
            }
        } catch (error) {
            console.error('Error saving tenant:', error);
            alert(`Failed to save tenant: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNewUserChange = (e) => {
        const { name, value } = e.target;
        setNewUserData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setIsAddingUser(true);
        
        try {
            const response = await fetch(`/api/admin/tenants/${tenantId}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    email: newUserData.email,
                    firstName: newUserData.firstName,
                    lastName: newUserData.lastName,
                    role: newUserData.role
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                // Add the new user to the local state
                setUsers(prevUsers => [...prevUsers, data.user]);
                
                // Reset form and close modal
                setNewUserData({
                    email: '',
                    firstName: '',
                    lastName: '',
                    role: 'user'
                });
                setShowAddUser(false);
            } else {
                throw new Error(data.message || 'Failed to add user');
            }
        } catch (error) {
            console.error('Error adding user:', error);
            alert(`Failed to add user: ${error.message}`);
        } finally {
            setIsAddingUser(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        navigate('/');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-6" />
                    <p className="text-xl font-semibold text-gray-700 mb-2">Loading Tenant Details</p>
                    <p className="text-gray-500">Fetching tenant information...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <img src={logo} alt="SureCost" className="h-8 w-auto" />
                        <div className="h-6 w-px bg-gray-300" />
                        <nav className="flex items-center space-x-2 text-sm">
                            <button
                                onClick={() => navigate('/admin')}
                                className="text-gray-500 hover:text-gray-700"
                                style={{ all: 'unset', cursor: 'pointer' }}
                            >
                                Admin Panel
                            </button>
                            <span className="text-gray-300">/</span>
                            <span className="text-gray-900 font-medium">Tenant Details</span>
                        </nav>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        <button 
                            onClick={handleLogout}
                            className="text-sm text-gray-600 hover:text-gray-900"
                            style={{ all: 'unset', cursor: 'pointer' }}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto p-4">
                {/* Page Header */}
                <div className="mb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Tenant Details</h1>
                            <p className="text-gray-600 text-sm">Manage tenant information and settings</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin')}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none"
                        >
                            ← Back to Admin Panel
                        </button>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Top Row: Overview and Tenant Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Tenant Overview */}
                        <div className="bg-white shadow rounded border border-gray-200 p-3">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Tenant Overview</h3>
                            <div className="grid grid-cols-4 gap-4">
                                <div className="text-center">
                                    <span className="text-xs font-medium text-gray-500 block">Tenant ID</span>
                                    <p className="text-sm font-semibold text-gray-900">{tenant?.id}</p>
                                </div>
                                <div className="text-center">
                                    <span className="text-xs font-medium text-gray-500 block">Total Users</span>
                                    <p className="text-sm font-semibold text-gray-900">{users.length}</p>
                                </div>
                                <div className="text-center">
                                    <span className="text-xs font-medium text-gray-500 block">Created</span>
                                    <p className="text-sm font-semibold text-gray-900">{new Date(tenant?.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="text-center">
                                    <span className="text-xs font-medium text-gray-500 block">Last Updated</span>
                                    <p className="text-sm font-semibold text-gray-900">{new Date(tenant?.updated_at).toLocaleDateString()}</p>
                                </div>
                            </div>
                        </div>

                        {/* Tenant Information Form */}
                        <div className="bg-white shadow rounded border border-gray-200">
                            <div className="px-4 py-3 border-b border-gray-200">
                                <h2 className="text-sm font-medium text-gray-900">Tenant Information</h2>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Tenant Name */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Tenant Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter tenant name"
                                            required
                                        />
                                    </div>

                                    {/* Domain */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Domain
                                        </label>
                                        <input
                                            type="text"
                                            name="domain"
                                            value={formData.domain}
                                            onChange={handleChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="example.com"
                                            required
                                        />
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="suspended">Suspended</option>
                                        </select>
                                    </div>

                                    {/* Admin Checkbox */}
                                    <div className="flex items-center pt-4">
                                        <input
                                            type="checkbox"
                                            name="isAdmin"
                                            id="isAdmin"
                                            checked={formData.isAdmin}
                                            onChange={handleChange}
                                            className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="isAdmin" className="ml-2 block text-xs text-gray-700">
                                            Admin Tenant
                                        </label>
                                    </div>
                                </div>

                                {/* Vendor Dashboard - Default Vendor Filter */}
                                <div className="mt-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Default Vendor Filter
                                    </label>
                                    <select
                                        name="defaultVendorId"
                                        value={formData.defaultVendorId}
                                        onChange={handleChange}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">All Vendors</option>
                                        {vendors.map(vendor => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Sets the default vendor filter when this tenant accesses the dashboard
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin')}
                                        style={{
                                            padding: '0.375rem 0.75rem',
                                            fontSize: '0.75rem',
                                            fontWeight: '500',
                                            color: '#374151',
                                            backgroundColor: 'white',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '0.25rem',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        style={{
                                            padding: '0.375rem 1rem',
                                            fontSize: '0.75rem',
                                            fontWeight: '500',
                                            color: 'white',
                                            backgroundColor: isSaving ? '#1d4ed8' : '#2563eb',
                                            border: 'none',
                                            borderRadius: '0.25rem',
                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                            opacity: isSaving ? 0.5 : 1
                                        }}
                                        onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#1d4ed8')}
                                        onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = '#2563eb')}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Bottom Row: Users List */}
                    <div className="bg-white shadow rounded border border-gray-200 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-gray-900">Users ({users.length})</h3>
                            <button
                                onClick={() => setShowAddUser(true)}
                                style={{
                                    padding: '0.375rem 0.75rem',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    color: 'white',
                                    backgroundColor: '#2563eb',
                                    border: 'none',
                                    borderRadius: '0.25rem',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
                            >
                                + Add User
                            </button>
                        </div>

                        {/* Add User Form */}
                        {showAddUser && (
                            <div className="mb-4 p-3 bg-gray-50 rounded border">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Add New User</h4>
                                <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={newUserData.email}
                                            onChange={handleNewUserChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="user@example.com"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={newUserData.firstName}
                                            onChange={handleNewUserChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="John"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={newUserData.lastName}
                                            onChange={handleNewUserChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="Doe"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                                        <select
                                            name="role"
                                            value={newUserData.role}
                                            onChange={handleNewUserChange}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                            <option value="viewer">Viewer</option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-2 lg:col-span-4 flex items-center justify-end space-x-2 mt-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAddUser(false);
                                                setNewUserData({
                                                    email: '',
                                                    firstName: '',
                                                    lastName: '',
                                                    role: 'user'
                                                });
                                            }}
                                            style={{
                                                padding: '0.375rem 0.75rem',
                                                fontSize: '0.75rem',
                                                fontWeight: '500',
                                                color: '#374151',
                                                backgroundColor: 'white',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '0.25rem',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isAddingUser}
                                            style={{
                                                padding: '0.375rem 1rem',
                                                fontSize: '0.75rem',
                                                fontWeight: '500',
                                                color: 'white',
                                                backgroundColor: isAddingUser ? '#059669' : '#10b981',
                                                border: 'none',
                                                borderRadius: '0.25rem',
                                                cursor: isAddingUser ? 'not-allowed' : 'pointer',
                                                opacity: isAddingUser ? 0.5 : 1
                                            }}
                                            onMouseEnter={(e) => !isAddingUser && (e.target.style.backgroundColor = '#059669')}
                                            onMouseLeave={(e) => !isAddingUser && (e.target.style.backgroundColor = '#10b981')}
                                        >
                                            {isAddingUser ? 'Adding...' : 'Add User'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                            {users.length > 0 ? (
                                users.map((user) => (
                                    <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{user.email}</p>
                                            <p className="text-gray-500 truncate">
                                                {user.first_name} {user.last_name} • {user.role}
                                            </p>
                                        </div>
                                        <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ml-2 ${
                                            user.status === 'active' 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {user.status}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full">
                                    <p className="text-xs text-gray-500 text-center py-6">No users found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TenantDetails;
